import React, { useState } from "react";
import {
  Card,
  Text,
  Group,
  Box,
  Title,
  Button,
  Badge,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import AddOrgLevelTypeModal from "./AddOrgLevelTypeModal";
import EditOrgLevelTypeModal from "./EditOrgLevelTypeModal";

const OrgLevelTypeNodeBox = ({ node, onEdit }) => {
  const isBase = node.base_org_level_type;
  const isTop = node.top_org_level_type;
  const isEmphasized = isBase || isTop;

  return (
    <Box
      style={{
        padding: ".5rem .75rem",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        whiteSpace: "nowrap",
      }}
    >
      <Group gap="xs" align="center" wrap="nowrap" justify="center">
        <Text style={{ fontWeight: isEmphasized ? 700 : 500 }}>
          {node.name}
        </Text>
        {isBase && (
          <Badge size="xs" color="grape" variant="light">
            Base
          </Badge>
        )}
        {isTop && (
          <Badge size="xs" color="teal" variant="light">
            Top
          </Badge>
        )}
        {onEdit && (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onEdit(node)}
            aria-label={`Edit ${node.name}`}
          >
            <IconEdit size={16} />
          </ActionIcon>
        )}
      </Group>
      {isTop && (
        <Text
          size="xs"
          c="dimmed"
          mt={4}
          ta="center"
          style={{ whiteSpace: "normal", maxWidth: 220 }}
        >
          * The Top sales group type is for your entire company and cannot be
          deleted.
        </Text>
      )}
      {isBase && (
        <Text
          size="xs"
          c="dimmed"
          mt={4}
          ta="center"
          style={{ whiteSpace: "normal", maxWidth: 220 }}
        >
          * The Base sales group type is for the groups that reps are added to
          and it cannot be deleted.
        </Text>
      )}
    </Box>
  );
};

const OrgLevelTypeTreeNode = ({ node, onEdit }) => {
  const hasChildren = node.sub_types && node.sub_types.length > 0;

  return (
    <Box
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 .5rem",
      }}
    >
      <OrgLevelTypeNodeBox node={node} onEdit={onEdit} />
      {hasChildren && (
        <>
          <Box style={{ width: 2, height: 20, backgroundColor: "#ccc" }} />
          <Box style={{ display: "flex", alignItems: "flex-start" }}>
            {node.sub_types.map((child, i) => {
              const isFirst = i === 0;
              const isLast = i === node.sub_types.length - 1;
              const showHorizontal = node.sub_types.length > 1;

              return (
                <Box
                  key={child.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                    paddingTop: 20,
                  }}
                >
                  {showHorizontal && (
                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        left: isFirst ? "50%" : 0,
                        right: isLast ? "50%" : 0,
                        height: 2,
                        backgroundColor: "#ccc",
                      }}
                    />
                  )}
                  <Box
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 2,
                      height: 20,
                      backgroundColor: "#ccc",
                    }}
                  />
                  <OrgLevelTypeTreeNode node={child} onEdit={onEdit} />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

const OrgLevelTypesCard = ({
  orgLevelTypes = [],
  typesHierarchy = null,
  onRefresh,
  embedded = false,
}) => {
  const { getVerifiedJWT } = useAuth();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrgLevelType, setSelectedOrgLevelType] = useState(null);

  const fetchOrgLevelTypes = async () => {
    if (onRefresh) await onRefresh();
  };

  const handleAddOrgLevelType = async newType => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/org-level/type"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        name: newType.name,
        ...(newType.parent_org_level_type_ids !== undefined && {
          parent_org_level_type_ids: newType.parent_org_level_type_ids,
        }),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create sales group type");
    }

    await fetchOrgLevelTypes();
  };

  const handleUpdateOrgLevelType = async updatedType => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(`/v1/org-level/type/${updatedType.id}`),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name: updatedType.name,
          ...(updatedType.parent_org_level_type_ids !== undefined && {
            parent_org_level_type_ids: updatedType.parent_org_level_type_ids,
          }),
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to update sales group type");
    }

    await fetchOrgLevelTypes();
  };

  const handleDeleteOrgLevelType = async orgLevelTypeId => {
    const jwt = await getVerifiedJWT();

    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(`/v1/org-level/type/${orgLevelTypeId}`),
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to delete sales group type");
    }

    await fetchOrgLevelTypes();
  };

  const handleEditClick = type => {
    setSelectedOrgLevelType(type);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedOrgLevelType(null);
  };

  const content = (
    <>
      <Group justify={embedded ? "flex-end" : "space-between"} mb="md">
        {!embedded && <Title order={3}>Sales Groups Structure</Title>}
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setAddModalOpen(true)}
          variant="filled"
          size="sm"
        >
          Add Sales Group Type
        </Button>
      </Group>

      {!orgLevelTypes || orgLevelTypes.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No sales group types configured yet
        </Text>
      ) : (
        typesHierarchy && (
          <Box
            style={{
              overflowX: "auto",
              paddingBottom: ".5rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <OrgLevelTypeTreeNode
              node={typesHierarchy}
              onEdit={handleEditClick}
            />
          </Box>
        )
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {content}
        </Card>
      )}

      <AddOrgLevelTypeModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddOrgLevelType}
        orgLevelTypes={orgLevelTypes}
      />

      <EditOrgLevelTypeModal
        opened={editModalOpen}
        onClose={handleEditClose}
        onSave={handleUpdateOrgLevelType}
        onDelete={handleDeleteOrgLevelType}
        orgLevelType={selectedOrgLevelType}
      />
    </>
  );
};

export default OrgLevelTypesCard;
